/**
 * CommunicationLogPage — Generic contact/communication logging
 *
 * Used by: CFSS (QP contacts), ALF (family contacts), Legal (client contacts)
 * Industry config via props.
 *
 * Route: /{industry}/communication
 * Scope: communication:log
 */

import React, { useState, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconMessage,
  IconPhone,
  IconUser,
  IconPlus,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_COMMUNICATION_LOGS = gql`
  query GetCommunicationLogs($limit: Int) {
    communicationLogs(limit: $limit) {
      id
      contactType
      participantId
      summary
      duration
      outcome
      createdAt
      createdBy
    }
  }
`;

const CREATE_COMMUNICATION_LOG = gql`
  mutation CreateCommunicationLog($input: CreateCommunicationLogInput!) {
    createCommunicationLog(input: $input) {
      id
      contactType
    }
  }
`;

interface CommunicationLog {
  id: string;
  contactType: string;
  participantId: string;
  summary: string;
  duration: number | null;
  outcome: string | null;
  createdAt: string;
  createdBy: string | null;
}

interface CommunicationLogPageProps {
  title?: string;
  subtitle?: string;
  subjectLabel?: string;
  scope?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function getContactIcon(type: string) {
  switch (type.toLowerCase()) {
    case 'phone': return IconPhone;
    case 'visit': return IconUser;
    default: return IconMessage;
  }
}

function getContactColor(type: string): string {
  switch (type.toLowerCase()) {
    case 'phone': return 'blue';
    case 'visit': return 'green';
    case 'email': return 'violet';
    default: return 'gray';
  }
}

export function CommunicationLogPage({
  title = 'Communication Log',
  subtitle = 'Document contacts and communications',
  subjectLabel = 'Subject',
  scope = 'communication:log',
}: CommunicationLogPageProps) {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newLog, setNewLog] = useState({
    contactType: 'phone',
    participantId: '',
    summary: '',
    duration: '',
    outcome: '',
  });

  const canLog = useHasScope(scope);

  const { data, loading, refetch } = useQuery<{ communicationLogs: CommunicationLog[] }>(
    GET_COMMUNICATION_LOGS,
    { variables: { limit: 100 } }
  );

  const [createLog, { loading: creating }] = useMutation(CREATE_COMMUNICATION_LOG);

  const logs = data?.communicationLogs ?? [];

  const today = new Date();
  const thisWeek = logs.filter(l => {
    const created = new Date(l.createdAt);
    return today.getTime() - created.getTime() < 7 * 24 * 60 * 60 * 1000;
  });

  const handleCreate = useCallback(async () => {
    if (!newLog.participantId || !newLog.summary) return;
    try {
      await createLog({
        variables: {
          input: {
            contactType: newLog.contactType,
            participantId: newLog.participantId,
            summary: newLog.summary,
            duration: newLog.duration ? parseInt(newLog.duration, 10) : null,
            outcome: newLog.outcome || null,
          },
        },
      });
      notifications.show({
        title: 'Contact Logged',
        message: 'The communication has been recorded.',
        color: 'green',
      });
      setCreateModalOpen(false);
      setNewLog({ contactType: 'phone', participantId: '', summary: '', duration: '', outcome: '' });
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Log Failed',
        message: err instanceof Error ? err.message : 'An error occurred.',
        color: 'red',
      });
    }
  }, [newLog, createLog, refetch]);

  if (loading) {
    return <Center h="60vh"><Loader size="lg" /></Center>;
  }

  if (!canLog) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertCircle />}>
          You do not have permission to log communications.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconMessage size={20} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={2}>{title}</Title>
              <Text size="sm" c="dimmed">{subtitle}</Text>
            </Stack>
          </Group>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateModalOpen(true)}>
            Log Contact
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">This Week</Text>
              <ThemeIcon size="sm" variant="light" color="blue"><IconMessage size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{thisWeek.length}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Phone Calls</Text>
              <ThemeIcon size="sm" variant="light" color="blue"><IconPhone size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{logs.filter(l => l.contactType === 'phone').length}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Visits</Text>
              <ThemeIcon size="sm" variant="light" color="green"><IconUser size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{logs.filter(l => l.contactType === 'visit').length}</Text>
          </Card>
        </SimpleGrid>

        {logs.length === 0 ? (
          <Alert color="blue" title="No Logs Yet" icon={<IconMessage />}>
            Click "Log Contact" to record a call or visit.
          </Alert>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>{subjectLabel}</Table.Th>
                  <Table.Th>Summary</Table.Th>
                  <Table.Th>Duration</Table.Th>
                  <Table.Th>Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logs.map(log => {
                  const Icon = getContactIcon(log.contactType);
                  return (
                    <Table.Tr key={log.id}>
                      <Table.Td>
                        <Badge color={getContactColor(log.contactType)} size="sm" leftSection={<Icon size={12} />}>
                          {log.contactType}
                        </Badge>
                      </Table.Td>
                      <Table.Td><Text size="sm" fw={500}>{log.participantId}</Text></Table.Td>
                      <Table.Td><Text size="sm" lineClamp={2}>{log.summary}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{log.duration ? `${log.duration} min` : '—'}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{formatDate(log.createdAt)}</Text></Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Modal opened={createModalOpen} onClose={() => setCreateModalOpen(false)} title="Log Communication" size="md">
        <Stack gap="md">
          <Select
            label="Contact Type"
            required
            data={[
              { value: 'phone', label: 'Phone Call' },
              { value: 'visit', label: 'In-Person Visit' },
              { value: 'email', label: 'Email' },
              { value: 'text', label: 'Text Message' },
              { value: 'other', label: 'Other' },
            ]}
            value={newLog.contactType}
            onChange={v => setNewLog(prev => ({ ...prev, contactType: v ?? 'phone' }))}
          />
          <Select
            label={subjectLabel}
            placeholder={`Select ${subjectLabel.toLowerCase()}`}
            required
            searchable
            data={[]}
            value={newLog.participantId}
            onChange={v => setNewLog(prev => ({ ...prev, participantId: v ?? '' }))}
          />
          <Textarea
            label="Summary"
            placeholder="Brief description of the contact..."
            required
            minRows={3}
            value={newLog.summary}
            onChange={e => setNewLog(prev => ({ ...prev, summary: e.currentTarget.value }))}
          />
          <Select
            label="Duration (minutes)"
            data={['5', '10', '15', '30', '45', '60', '90', '120'].map(v => ({ value: v, label: `${v} min` }))}
            value={newLog.duration}
            onChange={v => setNewLog(prev => ({ ...prev, duration: v ?? '' }))}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setCreateModalOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={handleCreate} loading={creating} disabled={!newLog.participantId || !newLog.summary}>
              Save Log
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default CommunicationLogPage;
